// Styles par rôle (docs/REFONTE-CHARTES.md §1) : le style appartient à la charte, par rôle ;
// l'éditeur ne règle que la structure. Ce module dit quel rôle porte chaque bloc, dérive les rôles
// des anciennes colonnes (exactement le rendu d'avant, c'est ce que la migration SQL écrit), et
// valide ce qui vient de la base sans jamais lever d'exception.
import type { Brand, BrandStyles, CustomRoleStyle, FontWeight, LayoutBlock, RoleId, RoleRef, SignatureLayout, TextRoleId, TextRoleStyle } from './types.ts';
import { safeColor } from './util.ts';

export const ROLE_IDS: readonly RoleId[] = ['name', 'title', 'body', 'legal', 'social', 'separator'];
export const TEXT_ROLE_IDS: readonly TextRoleId[] = ['name', 'title', 'body', 'legal'];

export const ROLE_LABELS: Record<RoleId, string> = {
  name: 'Nom',
  title: 'Fonction',
  body: 'Texte courant',
  legal: 'Mention légale',
  social: 'Icônes réseaux',
  separator: 'Traits',
};

export const ROLE_HINTS: Record<RoleId, string> = {
  name: 'Le nom de la personne',
  title: 'Le poste de la personne',
  body: 'Téléphones, e-mail, adresse, site web, texte libre',
  legal: 'Mention légale (Loi Évin…)',
  social: 'Instagram, Facebook, TikTok, LinkedIn',
  separator: 'Traits entre les colonnes et filets',
};

export const WEIGHT_LABELS: Record<FontWeight, string> = { normal: 'Normale', bold: 'Grasse' };
export const FONT_WEIGHTS: readonly FontWeight[] = ['normal', 'bold'];

/** Valeur CSS d'une graisse : jamais de 900 (aucune police proposée ne le possède ; chaque client le simulerait à sa façon). */
export const cssWeight = (w: FontWeight): string => (w === 'bold' ? 'bold' : 'normal');


/** Piles de polices proposées dans la fiche : liste fermée, toutes présentes sur Mac, Windows et iPhone (repli générique en dernier). */
export const FONT_STACKS: readonly { css: string; label: string }[] = [
  { css: 'Arial, Helvetica, sans-serif', label: 'Arial / Helvetica' },
  { css: "'Helvetica Neue', Helvetica, Arial, sans-serif", label: 'Helvetica Neue (Arial sur Windows)' },
  { css: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { css: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { css: "'Trebuchet MS', Helvetica, sans-serif", label: 'Trebuchet MS' },
  { css: 'Georgia, serif', label: 'Georgia (serif)' },
  { css: "'Times New Roman', Times, serif", label: 'Times New Roman (serif)' },
  { css: "'Courier New', Courier, monospace", label: 'Courier New (monospace)' },
];
/** Options du sélecteur : la liste fermée, plus la valeur actuelle de la charte si elle n'en fait pas partie (jamais perdue en silence). */
export function fontStackOptions(current: string | null | undefined): { css: string; label: string }[] {
  const cur = (current ?? '').trim();
  if (cur && !FONT_STACKS.some((f) => f.css === cur)) return [...FONT_STACKS, { css: cur, label: `Valeur actuelle : ${cur}` }];
  return [...FONT_STACKS];
}

/** Valeurs codées en dur dans l'ancien moteur, reprises comme valeurs initiales des rôles. */
export const DEFAULT_BODY_COLOR = '#1a1a1a';
export const LEGAL_COLOR = '#8a8a8a';
export const DEFAULT_ICON_SIZE = 24;

/**
 * Rôles dérivés des anciennes colonnes : exactement les couleurs et tailles que rendait le moteur
 * avant la refonte (name_color ?? primary, title_color ?? accent, body_color ?? #1a1a1a, mention en
 * gris fixe à 10 px, separator_color ?? accent). La migration SQL écrit les mêmes valeurs.
 */
export function stylesFromLegacy(b: Brand): BrandStyles {
  const primary = safeColor(b.primary_color, '#000000');
  const accent = safeColor(b.accent_color, '#7e8c8d');
  const text = (color: string, size: number, weight: FontWeight): TextRoleStyle => ({ color, size, weight, letterSpacing: 0, lineHeight: null });
  return {
    name: text(safeColor(b.name_color, primary), 14, 'bold'),
    title: text(safeColor(b.title_color, accent), 12, 'bold'),
    body: text(safeColor(b.body_color, DEFAULT_BODY_COLOR), 12, 'normal'),
    legal: text(LEGAL_COLOR, 10, 'normal'),
    social: { color: safeColor(b.social_color, '#000000'), size: DEFAULT_ICON_SIZE },
    separator: { color: safeColor(b.separator_color, accent) },
  };
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown, fallback: number, min: number, max: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;

function textRole(v: unknown, d: TextRoleStyle): TextRoleStyle {
  if (!isObj(v)) return { ...d };
  // « black » (ancienne « Très grasse ») est lu comme bold : c'est ce que tout client affichait déjà.
  const weight = v.weight === 'black' ? 'bold' : FONT_WEIGHTS.includes(v.weight as FontWeight) ? (v.weight as FontWeight) : d.weight;
  const lh = v.lineHeight;
  const italic = typeof v.italic === 'boolean' ? v.italic : !!d.italic;
  return {
    color: safeColor(typeof v.color === 'string' ? v.color : null, d.color),
    size: Math.round(num(v.size, d.size, 6, 60)),
    weight,
    // Émis seulement à vrai : les styles en base et les oracles restent identiques sans italique.
    ...(italic ? { italic: true } : {}),
    letterSpacing: Math.round(num(v.letterSpacing, d.letterSpacing, -3, 10) * 10) / 10,
    lineHeight: typeof lh === 'number' && Number.isFinite(lh) && lh > 0 ? Math.round(Math.min(80, Math.max(6, lh))) : null,
  };
}

/** Identifiant d'un rôle personnalisé : `x-` + 6 caractères, généré à la création, jamais dérivé du nom. */
export const CUSTOM_ROLE_ID = /^x-[a-z0-9]{4,12}$/;
export const isCustomRole = (ref: string | null | undefined): boolean => !!ref && CUSTOM_ROLE_ID.test(ref);
export const CUSTOM_ROLE_NAME_MAX = 40;

export function newCustomRoleId(styles: BrandStyles): string {
  const taken = new Set(Object.keys(styles.custom ?? {}));
  for (;;) {
    const id = 'x-' + Math.random().toString(36).slice(2, 8).padEnd(6, '0');
    if (!taken.has(id)) return id;
  }
}

/** Rôles personnalisés lus depuis la base : clé valide, nom non vide, propriétés complétées depuis le texte courant. */
function customRoles(v: unknown, body: TextRoleStyle): Record<string, CustomRoleStyle> | undefined {
  if (!isObj(v)) return undefined;
  const out: Record<string, CustomRoleStyle> = {};
  for (const [id, raw] of Object.entries(v)) {
    if (!CUSTOM_ROLE_ID.test(id) || !isObj(raw)) continue;
    const name = typeof raw.name === 'string' ? raw.name.trim().slice(0, CUSTOM_ROLE_NAME_MAX) : '';
    if (!name) continue;
    out[id] = { ...textRole(raw, body), name };
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Styles exploitables quoi qu'il y ait en base : chaque champ manquant ou invalide reprend la valeur
 * dérivée des anciennes colonnes. Jamais d'exception.
 */
export function normalizeStyles(input: unknown, fallback: BrandStyles): BrandStyles {
  if (!isObj(input)) return fallback;
  const social = isObj(input.social) ? input.social : {};
  const separator = isObj(input.separator) ? input.separator : {};
  const body = textRole(input.body, fallback.body);
  const custom = customRoles(input.custom, body);
  return {
    name: textRole(input.name, fallback.name),
    title: textRole(input.title, fallback.title),
    body,
    legal: textRole(input.legal, fallback.legal),
    social: {
      color: safeColor(typeof social.color === 'string' ? social.color : null, fallback.social.color),
      size: Math.round(num(social.size, fallback.social.size, 8, 48)),
    },
    separator: { color: safeColor(typeof separator.color === 'string' ? separator.color : null, fallback.separator.color) },
    ...(custom ? { custom } : {}),
  };
}

/**
 * Style texte d'une référence de rôle : intégré, ou personnalisé par identifiant. Une référence
 * inconnue (rôle supprimé malgré tout, arbre venu d'ailleurs) retombe sur le texte courant : jamais
 * d'exception, jamais de bloc muet.
 */
export function textStyleOf(styles: BrandStyles, ref: RoleRef | null | undefined): TextRoleStyle {
  if (ref === 'name' || ref === 'title' || ref === 'body' || ref === 'legal') return styles[ref];
  if (ref && styles.custom?.[ref]) return styles.custom[ref];
  return styles.body;
}

/** Libellé d'une référence de rôle : nom intégré, ou nom donné par l'utilisateur. */
export function roleLabel(styles: BrandStyles, ref: RoleRef | RoleId | null | undefined): string {
  if (ref && ref in ROLE_LABELS) return ROLE_LABELS[ref as RoleId];
  if (ref && styles.custom?.[ref]) return styles.custom[ref].name;
  return ROLE_LABELS.body;
}

/** Rôles proposés à un bloc de texte libre : les quatre rôles texte intégrés, puis les rôles personnalisés (par nom). */
export function textRoleOptions(styles: BrandStyles): { id: RoleRef; label: string }[] {
  return [
    ...TEXT_ROLE_IDS.map((id) => ({ id, label: ROLE_LABELS[id] })),
    ...Object.entries(styles.custom ?? {}).map(([id, s]) => ({ id, label: s.name })),
  ];
}

/** Où un rôle personnalisé est utilisé dans la mise en page : les blocs de texte libre qui le référencent. */
export function customRoleUsage(layout: SignatureLayout, roleId: string): { blockId: string; where: string }[] {
  const out: { blockId: string; where: string }[] = [];
  layout.rows.forEach((r, i) =>
    r.cols.forEach((c, j) =>
      c.blocks.forEach((b) => {
        if (b.type === 'text' && b.role === roleId) out.push({ blockId: b.id, where: `Ligne ${i + 1}, colonne ${j + 1}` });
      })
    )
  );
  return out;
}

/**
 * Supprime un rôle personnalisé — REFUSÉ s'il est encore utilisé, avec l'endroit ; jamais de bloc
 * orphelin laissé en silence.
 */
export function removeCustomRole(styles: BrandStyles, layout: SignatureLayout, roleId: string): { ok: true; styles: BrandStyles } | { ok: false; reason: string } {
  const used = customRoleUsage(layout, roleId);
  if (used.length) {
    const name = styles.custom?.[roleId]?.name ?? roleId;
    return { ok: false, reason: `« ${name} » est encore utilisé par ${used.length} bloc${used.length > 1 ? 's' : ''} de texte libre (${used.map((u) => u.where).join(' ; ')}). Changez d’abord le rôle de ces blocs dans la mise en page.` };
  }
  const custom = { ...(styles.custom ?? {}) };
  delete custom[roleId];
  const next: BrandStyles = { ...styles };
  if (Object.keys(custom).length) next.custom = custom;
  else delete next.custom;
  return { ok: true, styles: next };
}

/** Les styles effectifs d'une charte : ceux enregistrés, complétés ; sinon dérivés des anciennes colonnes. */
export function effectiveStyles(brand: Brand): BrandStyles {
  return normalizeStyles(brand.styles, stylesFromLegacy(brand));
}

/** Rôle d'un bloc — correspondance fixe (§1.1) ; un texte libre référence un rôle intégré ou personnalisé. null = image sans style. */
export function blockRole(block: LayoutBlock): RoleId | RoleRef | null {
  switch (block.type) {
    case 'field':
      if (block.field === 'name') return 'name';
      if (block.field === 'title') return 'title';
      if (block.field === 'legal') return 'legal';
      return 'body';
    case 'text':
      return block.role ?? 'body';
    case 'social':
      return 'social';
    case 'divider':
      return 'separator';
    default:
      return null;
  }
}

const fmtPx = (n: number) => `${n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} px`;

/** Résumé lisible d'un rôle texte pour le panneau : « 14 px · Très grasse · +0,3 px ». */
export function roleSummary(styles: BrandStyles, role: RoleId | RoleRef): string {
  if (role === 'social') return `${styles.social.size} px`;
  if (role === 'separator') return 'couleur des traits';
  const s = textStyleOf(styles, role);
  const parts = [fmtPx(s.size), WEIGHT_LABELS[s.weight]];
  if (s.italic) parts.push('Italique');
  if (s.letterSpacing) parts.push(`${s.letterSpacing > 0 ? '+' : ''}${fmtPx(s.letterSpacing)}`);
  if (s.lineHeight) parts.push(`interligne ${fmtPx(s.lineHeight)}`);
  return parts.join(' · ');
}

/** Couleur d'un rôle (pastille du panneau). */
export function roleColor(styles: BrandStyles, role: RoleId | RoleRef): string {
  if (role === 'social' || role === 'separator') return styles[role].color;
  return textStyleOf(styles, role).color;
}

/** Seuil sous lequel Outlook Windows rend l'interlettrage négatif de façon incertaine. */
export const LETTER_SPACING_WARN_BELOW = -0.5;
export const LETTER_SPACING_OUTLOOK_NOTE =
  'En dessous de -0,5 px, Outlook Windows (2003 à 2019) rend l’interlettrage négatif différemment de la norme : résultat incertain.';
